/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import {
  booleanAttribute,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnInit,
  Output,
  QueryList,
  signal,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core'
import { PidxLoaderModeEnum, PidxPdsModalWrapperService } from '@myprofile-ui/pidx-ui-components'
import { PidxErrorService, PidxScrollToService, WINDOW } from '@myprofile-ui/pidx-utils'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { TranslateService } from '@ngx-translate/core'
import { AsyncSubject, EMPTY, lastValueFrom } from 'rxjs'
import { catchError, take } from 'rxjs/operators'
import { MyProfileBirthdayEditformComponent } from './components/myprofile-birthday-editform/myprofile-birthday-editform.component'
import { MyprofileBirthdayWidgetMode } from './myprofile-birthday-widget-mode'
import { MyProfileBirthdayService } from './services/myprofile-birthday.service'
import { RESTRICT_GLOBAL_UI_CHANGES } from '../../../pidx-utils/src/lib/helpers/restrictGlobalUIChanges.injectionToken'
import { DateOfBirthDto, DateOfBirthService } from '../../../../apps/myprofile-ui/src/app/services/date-of-birth/date-of-birth.service'
import { ProfileService, PublicProfile } from '../../../../apps/myprofile-ui/src/app/services/profile/profile.service'
import { v4 as uuid } from 'uuid'
import { HttpHeaders } from '@angular/common/http'
import moment from 'moment'

@UntilDestroy()
@Component({
  selector: 'myprofile-birthday-widget-ng',
  templateUrl: './myprofile-birthday-widget.component.html',
  host: { class: 'profile:block profile:relative' },
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfileBirthdayWidgetComponent implements OnInit {
  private _window = inject<Window>(WINDOW)
  private _pidxErrorService = inject(PidxErrorService)
  private _myProfileBirthdayService = inject(MyProfileBirthdayService)
  private _dateOfBirthService = inject(DateOfBirthService)
  private _profileService = inject(ProfileService)
  private _translateService = inject(TranslateService)
  private _scrollToService = inject(PidxScrollToService)
  private _modalService = inject(PidxPdsModalWrapperService)
  private _headers!: HttpHeaders

  @Input() locale!: string
  @Input() country!: string
  @Input() mode: MyprofileBirthdayWidgetMode = MyprofileBirthdayWidgetMode.EXTERNAL
  @Input() token!: string
  @Output() birthdayChanged = new EventEmitter<string>()
  @Input() scrollOnAddNew = false

  /**
   * When `true`, the widget announces its own initial-load status via a polite
   * live region using its bundled translation pipeline (translated key
   * `birthdayWidget.general.loadingStatus`). Default `false` keeps the widget
   * silent during initial load so a host page can orchestrate one global
   * announcement; set to `true` for standalone embeddings.
   *
   * Does not gate the saving overlay (always announced because it is
   * user-initiated).
   */
  public announceLoading = input(false, { transform: booleanAttribute })

  @ViewChildren(MyProfileBirthdayEditformComponent, { read: ElementRef }) public birthdayEditFormQL!: QueryList<ElementRef>

  public MyProfileBirthdayWidgetMode = MyprofileBirthdayWidgetMode
  public isEditMode = signal<boolean>(false)
  public isLoading = signal<boolean>(true)
  public isRefreshing = signal<boolean>(false)
  public errorMessage = signal<string>('')
  public fetchDateOfBirthRequestErrorResponse = signal<string>('')
  public formattedDate = signal<string>('')
  public dateToEdit = signal<string>('')
  // Set minDate as a string in YYYY-MM-DD format (100 years ago from today)
  public minDate = signal<string>(moment().subtract(100, 'years').format('YYYY-MM-DD'))
  // Set maxDate as a string in YYYY-MM-DD format (today)
  public maxDate = signal<string>(moment().format('YYYY-MM-DD'))
  public isCnUser = signal<boolean>(false)
  public errorWhileInitializing = signal(false)
  public dateOfBirthFromProfile: string | undefined = undefined
  private _momentSubject = new AsyncSubject<any>()
  public restrictGlobalUIChanges = inject<boolean>(RESTRICT_GLOBAL_UI_CHANGES)

  /** Control id to return focus to when closing the editor (add / edit). */
  private focusReturnIdAfterClose: string | null = null
  /** Page-level fallback id (e.g. completion-status CTA) used when no in-widget origin button is rendered. */
  private externalFocusReturnId: string | null = null

  public async ngOnInit() {
    const momentImport = await import('moment/min/moment-with-locales')
    const momentWithLocales: any = (momentImport as any).default ?? momentImport
    this._momentSubject.next(momentWithLocales)
    this._momentSubject.complete()

    try {
      await this._myProfileBirthdayService.setInitialConfiguration(this.country, this.locale, this.mode)
      if (this.mode === MyprofileBirthdayWidgetMode.EXTERNAL) {
        this._headers = new HttpHeaders({
          Authorization: `Bearer ${this.token}`,
          'X-TRACE-ID': `MYPROFILE-BIRTHDAY-WIDGET-${uuid()}`,
        })
      }
      this.fetchProfile()
      this.fetchDateOfBirth()
    } catch (error) {
      this.errorWhileInitializing.set(true)
    } finally {
      this.isLoading.set(false)
    }
  }

  public async formatDateForUI(dateOfBirth: string): Promise<string> {
    const moment = await lastValueFrom(this._momentSubject)
    // Map no_NO to nb_NO -> see PROFILE-7623
    this.locale = this.locale === 'no_NO' ? 'nb_NO' : this.locale

    const currentDateFormat = moment.localeData(this.locale).longDateFormat('L')
    return dateOfBirth ? moment(dateOfBirth, 'YYYYMMDD').format(currentDateFormat) : ''
  }

  public async formatDateForBackend(dateOfBirth: string): Promise<string> {
    const moment = await lastValueFrom(this._momentSubject)
    return moment(dateOfBirth, 'YYYY-MM-DD', true).format('YYYYMMDD')
  }

  public fetchProfile() {
    return this._profileService
      .getProfile(this.country, this.locale, this._headers)
      .pipe(
        untilDestroyed(this),
        catchError((error) => {
          this.fetchDateOfBirthRequestErrorResponse.set(this._pidxErrorService.getErrorKeyForHttpErrorResponseByCode(error))
          return EMPTY
        }),
      )
      .subscribe(async (profile: PublicProfile | undefined) => {
        if (!profile) {
          return
        }
        this.isCnUser.set(['CN'].includes(profile.preferredCountry ?? ''))
        this.dateOfBirthFromProfile = profile.dateOfBirth
      })
  }

  public fetchDateOfBirth() {
    return this._dateOfBirthService
      .getDateOfBirthForProfile(this.country, this.locale, this._headers)
      .pipe(
        untilDestroyed(this),
        catchError((error) => {
          this.fetchDateOfBirthRequestErrorResponse.set(this._pidxErrorService.getErrorKeyForHttpErrorResponseByCode(error))
          return EMPTY
        }),
      )
      .subscribe(async (dateOfBirth: DateOfBirthDto | undefined) => {
        this.formattedDate.set(dateOfBirth && dateOfBirth.dateOfBirth ? await this.formatDateForUI(dateOfBirth.dateOfBirth) : '')
        this.dateToEdit.set(dateOfBirth && dateOfBirth.dateOfBirth ? dateOfBirth.dateOfBirth : '')
        if (this.mode === MyprofileBirthdayWidgetMode.EXTERNAL) {
          this.birthdayChanged.emit(this.formattedDate())
        }
      })
  }

  public setEditMode(open: boolean, originId?: string, externalFocusReturnId?: string) {
    if (open && originId) {
      this.focusReturnIdAfterClose = originId
    }
    if (open && externalFocusReturnId) {
      this.externalFocusReturnId = externalFocusReturnId
    }
    this.isEditMode.set(open)
    if (open) {
      this.birthdayEditFormQL.changes.pipe(take(1)).subscribe((components: QueryList<ElementRef>) => {
        this._window.setTimeout(() => {
          const host = components.first.nativeElement as HTMLElement
          if (this.scrollOnAddNew) {
            this._scrollToService.scrollToElement(host, 24)
          }
          host.focus()
        }, 0)
      })
    } else {
      this.restoreFocusAfterLeavingEditor()
    }
  }

  public async triggerDelete() {
    const title = this._translateService.instant('pidp.deleteDateOfBirth.modal.headline')
    const body = this._translateService.instant('pidp.deleteDateOfBirth.modal.subline')
    const btnPrimary = this._translateService.instant('pidp.deleteDateOfBirth.modal.primary')
    const btnGhost = this._translateService.instant('birthdayWidget.button.cancel.label')
    this._modalService.openModal({ title, body, btnPrimary, btnGhost }).then((value) => {
      if (value) {
        this.isRefreshing.set(true)
        this._dateOfBirthService
          .deleteDateOfBirthForProfile(this.country, this.locale, this._headers)
          .pipe(
            catchError((error) => {
              this.isRefreshing.set(false)
              this.errorMessage.set(this._pidxErrorService.getErrorKeyForHttpErrorResponseByCode(error))
              return error
            }),
          )
          .subscribe(() => {
            this.isRefreshing.set(false)
            this.formattedDate.set('')
            this.dateToEdit.set('')
            this.birthdayChanged.emit(this.formattedDate())
            this.dateOfBirthFromProfile = undefined
            this.focusElementById('addBirthdateBtn')
          })
      } else {
        this.focusElementById('deleteBirthdateBtn')
      }
    })
  }

  public async triggerSave(newDateOfBirth: string): Promise<void> {
    this.isRefreshing.set(true)
    const formattedForBackend = await this.formatDateForBackend(newDateOfBirth)
    const dateOfBirthDto: DateOfBirthDto = { dateOfBirth: formattedForBackend }

    this.dateOfBirthFromProfile
      ? this._dateOfBirthService
          .updateDateOfBirthForProfile(this.country, this.locale, dateOfBirthDto, this._headers)
          .pipe(
            catchError((error) => {
              this.isRefreshing.set(false)
              this.errorMessage.set(this._pidxErrorService.getErrorKeyForHttpErrorResponseByCode(error))
              return error
            }),
          )
          .subscribe(async () => {
            this.isRefreshing.set(false)
            this.setEditMode(false)
            this.formattedDate.set(await this.formatDateForUI(formattedForBackend))
            this.dateToEdit.set(formattedForBackend)
            this.dateOfBirthFromProfile = this.formattedDate()
            this.birthdayChanged.emit(this.formattedDate())
          })
      : this._dateOfBirthService
          .setDateOfBirthForProfile(this.country, this.locale, dateOfBirthDto, this._headers)
          .pipe(
            catchError((error) => {
              this.isRefreshing.set(false)
              this.errorMessage.set(this._pidxErrorService.getErrorKeyForHttpErrorResponseByCode(error))
              return error
            }),
          )
          .subscribe(async () => {
            this.isRefreshing.set(false)
            this.setEditMode(false)
            this.formattedDate.set(await this.formatDateForUI(formattedForBackend))
            this.dateOfBirthFromProfile = this.formattedDate()
            this.birthdayChanged.emit(this.formattedDate())
            this.dateToEdit.set(formattedForBackend)
          })
  }

  private restoreFocusAfterLeavingEditor() {
    if (!this.focusReturnIdAfterClose && !this.externalFocusReturnId) {
      return
    }
    const primaryId = this.focusReturnIdAfterClose
    const externalId = this.externalFocusReturnId
    this.focusReturnIdAfterClose = null
    this.externalFocusReturnId = null
    this._window.setTimeout(() => {
      const candidates: string[] = []
      if (primaryId) {
        candidates.push(primaryId)
        if (primaryId === 'addBirthdateBtn') {
          candidates.push(this.mode === MyprofileBirthdayWidgetMode.PROFILE ? 'editBtn-profile' : 'editBtn-external')
        }
      }
      if (externalId) {
        candidates.push(externalId)
      }
      for (const candidateId of candidates) {
        const el = document.getElementById(candidateId)
        if (el) {
          el.focus()
          return
        }
      }
    }, 0)
  }

  private focusElementById(id: string) {
    this._window.setTimeout(() => {
      document.getElementById(id)?.focus()
    }, 0)
  }

  protected readonly PidxLoaderModeEnum = PidxLoaderModeEnum
}
